import { Component, OnInit } from '@angular/core';
import { AuthService } from '../../auth/AuthService';

type GuidelineType = 'TEAM' | 'AUDIT';

interface GuidelineItem {
  title: string;
  description: string;
}

@Component({
  selector: 'app-guidelines',
  templateUrl: './guidelines.component.html',
  styleUrls: ['./guidelines.component.css']
})
export class GuidelinesComponent implements OnInit {
  activeClientId: number | null = null;
  showGuidelineModal = false;
  guidelineType: GuidelineType = 'TEAM';
  guidelineTitle = '';
  guidelineDescription = '';

  teamGuidelines: GuidelineItem[] = [
    {
      title: 'Start with the correct project',
      description: 'Confirm the selected client/project before opening or updating a patient. All work, shortcuts and saved information must belong to the active project.'
    },
    {
      title: 'Complete every applicable verification section',
      description: 'Review Appointment Info, Max & Deductible, Co-Insurance, Coverage & Frequency, Ortho and History. Save only verified information and make the assigned patient VERIFIED when the work is complete.'
    },
    {
      title: 'Use keyboard shortcuts carefully',
      description: 'Ctrl+D copies supported section values and Ctrl+Shift+V pastes them. Re-check the pasted values against the payer response before saving.'
    },
    {
      title: 'Coverage & Frequency shortcuts',
      description: 'Copy/paste is supported for Percent, Frequency, Guidelines and Age Limit fields.'
    },
    {
      title: 'Ortho shortcuts',
      description: 'Copy/paste is supported for Ortho Percent, Ortho Frequency and Ortho Guidelines.'
    },
    {
      title: 'Appointment, Max/Deductible, Co-Insurance and History shortcuts',
      description: 'Appointment supports Procedure Input. Max & Deductible supports Max Input, Coverage, Plan and Service. Co-Insurance supports Percent, Waiting Period and Deductible. History supports History Input and Eligibility.'
    },
    {
      title: 'Do',
      description: 'Use source information from the payer/portal, validate member and patient details, add meaningful remarks where clarification is required, and re-check values before updating the form.'
    },
    {
      title: 'Do not',
      description: 'Do not guess missing benefits, overwrite verified values without evidence, work under the wrong client, leave an assigned completed patient pending, or treat copied values as verified without checking the source.'
    }
  ];

  auditGuidelines: GuidelineItem[] = [
    {
      title: 'Audit against source evidence',
      description: 'Compare the breakdown entered in EVM with the payer/portal evidence available for the patient. Audit the value that was entered, not an assumed expected value.'
    },
    {
      title: 'Apply the project questionnaire',
      description: 'Use the active project/client audit questionnaire. Category weightage, question weightage, severity and audit criteria may differ by project.'
    },
    {
      title: 'Category and question weightage',
      description: 'Use the configured category weightage and its distributed question weightage. The total configured category weightage for a project must not exceed 100.'
    },
    {
      title: 'Severity',
      description: 'Record the configured severity for the failed question and use the question/category definition consistently when identifying an error.'
    },
    {
      title: 'Validate the full breakdown',
      description: 'Check the applicable patient information, maximums/deductibles, co-insurance, coverage/frequency, ortho and history fields before completing the audit.'
    },
    {
      title: 'Record objective audit remarks',
      description: 'Remarks should identify the field/question, expected source value, entered value and the reason for the finding. Avoid vague or subjective comments.'
    },
    {
      title: 'Do not alter criteria during an audit',
      description: 'Do not change weightage, severity or interpretation to fit a specific case. Criteria changes should be made through the project guideline/questionnaire process by an authorized client-edit user.'
    }
  ];

  constructor(public authService: AuthService) {}

  ngOnInit(): void {
    this.activeClientId = this.readClientId();
    this.loadProjectGuidelines();
  }

  get canManageGuidelines(): boolean {
    return this.authService.hasAnyRole(['client-edit']);
  }

  openAddGuideline(type: GuidelineType): void {
    if (!this.canManageGuidelines) {
      return;
    }

    this.guidelineType = type;
    this.guidelineTitle = '';
    this.guidelineDescription = '';
    this.showGuidelineModal = true;
  }

  closeGuidelineModal(): void {
    this.showGuidelineModal = false;
    this.guidelineTitle = '';
    this.guidelineDescription = '';
  }

  addGuideline(): void {
    if (!this.canManageGuidelines) {
      return;
    }

    const title = (this.guidelineTitle || '').trim();
    const description = (this.guidelineDescription || '').trim();

    if (!title || !description) {
      return;
    }

    const item: GuidelineItem = { title, description };

    if (this.guidelineType === 'AUDIT') {
      this.auditGuidelines = [...this.auditGuidelines, item];
    } else {
      this.teamGuidelines = [...this.teamGuidelines, item];
    }

    this.persistProjectGuidelines();
    this.closeGuidelineModal();
  }

  private readClientId(): number | null {
    const rawClientId = sessionStorage.getItem('clientId');

    if (!rawClientId) {
      return null;
    }

    try {
      return Number(JSON.parse(rawClientId));
    } catch (error) {
      return Number(rawClientId);
    }
  }

  private storageKey(): string {
    return 'evm-guidelines-' + (this.activeClientId != null ? this.activeClientId : 'default');
  }

  private loadProjectGuidelines(): void {
    const saved = localStorage.getItem(this.storageKey());

    if (!saved) {
      return;
    }

    try {
      const parsed = JSON.parse(saved);

      if (Array.isArray(parsed.teamGuidelines)) {
        this.teamGuidelines = parsed.teamGuidelines;
      }

      if (Array.isArray(parsed.auditGuidelines)) {
        this.auditGuidelines = parsed.auditGuidelines;
      }
    } catch (error) {
      console.warn('Unable to load saved project guidelines.', error);
    }
  }

  private persistProjectGuidelines(): void {
    localStorage.setItem(
      this.storageKey(),
      JSON.stringify({
        teamGuidelines: this.teamGuidelines,
        auditGuidelines: this.auditGuidelines
      })
    );
  }
}
